import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { showAPI, organizationAPI, riderAPI } from '../utils/api';
import api from '../utils/api';
import { Plus, PencilSimple, X, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { DashboardNav } from '../components/dashboard/DashboardNav';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { StatsGrid } from '../components/dashboard/StatsGrid';
import { ShowsGrid } from '../components/dashboard/ShowsGrid';
import { OrgMembersSection } from '../components/dashboard/OrgMembersSection';
import { StripeConnectSection } from '../components/dashboard/StripeConnectSection';
import { SquareConnectSection } from '../components/dashboard/SquareConnectSection';
import { RiderEntriesList } from '../components/dashboard/RiderEntriesList';
import { OrgEditorsSection } from '../components/dashboard/OrgEditorsSection';
import { KioskAccountsSection } from '../components/dashboard/KioskAccountsSection';
import { MasterAdminPanel } from '../components/dashboard/MasterAdminPanel';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [shows, setShows] = useState([]);
  const [allOrgs, setAllOrgs] = useState([]);
  const [activeOrgIdx, setActiveOrgIdx] = useState(0);
  const [orgMembers, setOrgMembers] = useState([]);
  const [myShowEntries, setMyShowEntries] = useState([]);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [connectingStripe, setConnectingStripe] = useState(false);
  const [payingShowId, setPayingShowId] = useState(null);
  const [editingOrg, setEditingOrg] = useState(null);
  const [editOrgName, setEditOrgName] = useState('');
  const [activeView, setActiveView] = useState(null);

  const userRoles = user?.roles || [user?.role];
  const isRider = userRoles.includes('RIDER');
  const isOrgOwner = userRoles.some(r => ['ORG_ADMIN', 'MASTER', 'LMASTER'].includes(r));
  const isEditor = (user?.editor_org_ids || []).length > 0;
  // Designated editors get the same Producer dashboard view as org owners so
  // they can see, open, and edit shows on the orgs they were added to.
  const isProducer = isOrgOwner || isEditor;
  const isMaster = userRoles.some(r => ['MASTER', 'LMASTER'].includes(r));

  // Initialize activeView once roles are resolved
  const effectiveView = activeView || (isProducer ? 'PRODUCER' : 'RIDER');
  const showProducerContent = effectiveView === 'PRODUCER' && isProducer;
  const showRiderContent = effectiveView === 'RIDER' && isRider;

  const organization = allOrgs[activeOrgIdx] || null;
  // MASTER users see all orgs system-wide, so don't show "+ Add Org" for them
  const canCreateMoreOrgs = !isMaster && isOrgOwner && allOrgs.length < 5;

  const loadData = useCallback(async () => {
    try {
      const promises = [showAPI.list()];
      if (isProducer) promises.push(organizationAPI.list());
      // Always load rider entries for any logged-in user (producers may enter shows too)
      promises.push(riderAPI.myEntriesByShow());

      const results = await Promise.all(promises);
      setShows(results[0].data);

      let idx = 1;
      if (isProducer) {
        const orgs = results[idx]?.data || [];
        setAllOrgs(orgs);
        if (activeOrgIdx >= orgs.length) setActiveOrgIdx(0);
        idx++;
      }
      setMyShowEntries(results[idx]?.data || []);
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProducer]);

  const loadOrgMembers = useCallback(async (orgId) => {
    if (!orgId) { setOrgMembers([]); return; }
    try {
      const { data } = await organizationAPI.getMembers(orgId);
      setOrgMembers(data);
    } catch { setOrgMembers([]); }
  }, []);

  const loadStripeStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/payments/stripe/connect/status-user');
      setStripeStatus(data);
    } catch { setStripeStatus(null); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    if (isProducer) loadStripeStatus();
  }, [isProducer, loadStripeStatus]);
  useEffect(() => {
    if (organization) loadOrgMembers(organization.id);
  }, [organization, loadOrgMembers]);

  const handleStripeOnboard = async () => {
    setConnectingStripe(true);
    try {
      const { data } = await api.post('/payments/stripe/connect/onboard-user', {
        origin_url: window.location.origin
      });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start Stripe onboarding');
    } finally {
      setConnectingStripe(false);
    }
  };

  const handleStripeDashboard = async () => {
    try {
      const { data } = await api.get('/payments/stripe/connect/dashboard-user');
      if (data.url) window.open(data.url, '_blank');
    } catch {
      toast.error('Failed to open Stripe dashboard');
    }
  };


  const handleRemoveMember = async (memberId) => {
    if (!organization) return;
    try {
      await organizationAPI.removeMember(organization.id, memberId);
      setOrgMembers(prev => prev.filter(m => m.id !== memberId));
      toast.success('Member removed');
    } catch { toast.error('Failed to remove'); }
  };

  const handlePayNow = async (showId) => {
    setPayingShowId(showId);
    try {
      const { data } = await riderAPI.checkoutShow(showId, window.location.origin);
      if (data.url) window.location.href = data.url;
    } catch { toast.error('Payment failed'); setPayingShowId(null); }
  };

  const handleDeleteShow = async (showId) => {
    try {
      await showAPI.delete(showId);
      setShows(prev => prev.filter(s => s.id !== showId));
      toast.success('Show deleted');
    } catch { toast.error('Failed to delete show'); }
  };

  const handleStartEditOrg = (org) => {
    setEditingOrg(org.id);
    setEditOrgName(org.name);
  };

  const handleSaveOrg = async () => {
    if (!editOrgName.trim()) { toast.error('Name is required'); return; }
    try {
      await organizationAPI.update(editingOrg, { name: editOrgName.trim() });
      setAllOrgs(prev => prev.map(o => o.id === editingOrg ? { ...o, name: editOrgName.trim() } : o));
      setEditingOrg(null);
      toast.success('Organization updated');
    } catch { toast.error('Failed to update'); }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <DashboardNav userName={user.name} onLogout={logout} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <DashboardHeader user={user} userRoles={userRoles} isMaster={isMaster} isRider={isRider} isProducer={isProducer} activeView={effectiveView} onToggleView={setActiveView} />

        {isMaster && <MasterAdminPanel />}

        {/* Organization Tabs */}
        {showProducerContent && isOrgOwner && allOrgs.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center gap-2 flex-wrap" data-testid="org-tabs">
              {allOrgs.map((org, idx) => (
                <div key={org.id} className="flex items-center">
                  {editingOrg === org.id ? (
                    <div className="flex items-center gap-1 bg-[#161616] border border-[#0096FF]/50 rounded-lg px-2 py-1.5">
                      <input
                        type="text" value={editOrgName} onChange={(e) => setEditOrgName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveOrg(); if (e.key === 'Escape') setEditingOrg(null); }}
                        className="bg-transparent text-white text-sm font-semibold outline-none w-40"
                        data-testid={`edit-org-name-input-${idx}`}
                        autoFocus
                      />
                      <button onClick={handleSaveOrg} className="p-1 hover:bg-[#34C759]/20 rounded" data-testid={`save-org-btn-${idx}`}><Check size={14} className="text-[#34C759]" /></button>
                      <button onClick={() => setEditingOrg(null)} className="p-1 hover:bg-[#FF3B30]/20 rounded"><X size={14} className="text-[#9E9E9E]" /></button>
                    </div>
                  ) : (
                    <div
                      onClick={() => setActiveOrgIdx(idx)}
                      className={`group flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer ${
                        idx === activeOrgIdx
                          ? 'bg-[#0096FF]/15 text-[#0096FF] border border-[#0096FF]/30'
                          : 'bg-[#161616] text-[#9E9E9E] border border-[#262626] hover:border-[#0096FF]/20 hover:text-white'
                      }`}
                      data-testid={`org-tab-${idx}`}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setActiveOrgIdx(idx); }}
                    >
                      {org.name}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleStartEditOrg(org); }}
                        className="p-0.5 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded transition-all"
                        data-testid={`edit-org-btn-${idx}`}
                      >
                        <PencilSimple size={12} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {canCreateMoreOrgs && (
                <button
                  onClick={() => navigate('/organization/setup')}
                  className="flex items-center gap-1 px-3 py-2 border border-dashed border-[#262626] rounded-lg text-[#555] text-sm hover:border-[#0096FF]/30 hover:text-[#0096FF] transition-all"
                  data-testid="add-org-btn"
                >
                  <Plus size={14} /> Add Org
                </button>
              )}
            </div>
            <p className="text-[10px] text-[#555] mt-1.5">{allOrgs.length}/5 organizations</p>
          </div>
        )}

        {/* No Org - Optional setup prompt (only for org owners; editors don't own an org).
            v265 — Solo producers can now create shows without an org, so this
            is a soft nudge, not a gate. The Create Show button still shows. */}
        {showProducerContent && isOrgOwner && allOrgs.length === 0 && (
          <div data-testid="org-setup-prompt" className="bg-[#161616] border border-[#262626] rounded-lg p-6 mb-6 flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <h2 className="text-lg font-bold text-white mb-1" style={{fontFamily: 'Outfit'}}>Set Up Your Organization (optional)</h2>
              <p className="text-[#9E9E9E] text-sm">Create one to run shows under a team, invite editors, and issue season points. You can also just run solo shows on your own.</p>
            </div>
            <button data-testid="setup-org-btn" onClick={() => navigate('/organization/setup')} className="px-4 py-2 bg-[#0A0A0A] border border-[#0096FF]/40 text-[#0096FF] hover:bg-[#0096FF]/10 font-semibold rounded-md transition-colors text-sm whitespace-nowrap">Create Organization</button>
          </div>
        )}

        {/* Stripe Connect — HIDDEN. Platform now routes all payments via Square (single-merchant mode). */}
        {false && showProducerContent && isOrgOwner && (
          <>
            <StripeConnectSection stripeStatus={stripeStatus} connectingStripe={connectingStripe} onStripeOnboard={handleStripeOnboard} onStripeDashboard={handleStripeDashboard} onStatusChange={loadStripeStatus} />
          </>
        )}
        {showProducerContent && isOrgOwner && (
          <SquareConnectSection />
        )}
        {/* v265 — Create Show button always available to producers (org or solo). */}
        {showProducerContent && isOrgOwner && (
          <>
            <div className="mb-8 flex flex-col sm:flex-row gap-3">
              <button data-testid="create-show-btn" onClick={() => navigate('/shows/create')} className="inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-semibold rounded-md transition-colors text-sm">
                <Plus size={18} weight="bold" /> Create New Show
              </button>
            </div>
          </>
        )}

        {/* Editor banner — for designated editors who don't own an org */}
        {showProducerContent && !isOrgOwner && isEditor && (
          <div className="mb-6 bg-[#FF9500]/10 border border-[#FF9500]/30 rounded-lg p-4" data-testid="editor-banner">
            <div className="flex items-start gap-3">
              <PencilSimple size={20} className="text-[#FF9500] flex-shrink-0 mt-0.5" weight="duotone" />
              <div>
                <h3 className="text-[#FF9500] font-bold text-sm mb-1">Show Editor Access</h3>
                <p className="text-[#9E9E9E] text-xs">
                  You&apos;ve been added as an editor on {user?.editor_org_ids?.length === 1 ? '1 organization' : `${user?.editor_org_ids?.length} organizations`}.
                  You can open any show below to edit details, add classes, manage entries, and update results.
                </p>
              </div>
            </div>
          </div>
        )}

        {showProducerContent && isOrgOwner && organization && (
            <OrgMembersSection
              organization={organization}
              orgMembers={orgMembers}
              onRemoveMember={handleRemoveMember}
              onMemberAdded={() => loadOrgMembers(organization.id)}
            />
        )}

        {showProducerContent && isOrgOwner && organization && (
            <OrgEditorsSection organization={organization} />
        )}

        {showProducerContent && (isOrgOwner || isEditor) && (
            <KioskAccountsSection shows={shows} />
        )}

        {showProducerContent && (
          <ShowsGrid shows={shows} isAdmin={true} onDelete={handleDeleteShow} />
        )}

        {showRiderContent && (
          <>
            <StatsGrid showsCount={shows.filter(s => ['PUBLISHED','LIVE','COMPLETED'].includes(s.state)).length} entriesCount={myShowEntries.reduce((sum, sg) => sum + sg.entries.length, 0)} isRider={isRider} organization={organization} />
            <RiderEntriesList myShowEntries={myShowEntries} payingShowId={payingShowId} onPayNow={handlePayNow} onRefresh={async () => { try { const { data } = await riderAPI.myEntriesByShow(); setMyShowEntries(data); } catch {} }} />
            {!showProducerContent && (
              <ShowsGrid shows={shows.filter(s => ['PUBLISHED','LIVE','COMPLETED'].includes(s.state))} isAdmin={false} />
            )}
          </>
        )}

        {/* Show entries for producers who also entered shows (not in rider view) */}
        {!showRiderContent && myShowEntries.length > 0 && (
          <RiderEntriesList myShowEntries={myShowEntries} payingShowId={payingShowId} onPayNow={handlePayNow} onRefresh={async () => { try { const { data } = await riderAPI.myEntriesByShow(); setMyShowEntries(data); } catch {} }} />
        )}

        {isMaster && (
          <div className="mt-6 flex flex-wrap gap-3">
            <button data-testid="manage-subscriptions-btn" onClick={() => navigate('/admin/subscriptions')} className="px-4 py-2 bg-[#AF52DE]/15 border border-[#AF52DE]/30 text-[#AF52DE] font-semibold rounded-md transition-colors text-sm">
              Manage Subscriptions
            </button>
            <button data-testid="platform-settings-btn" onClick={() => navigate('/admin/platform-settings')} className="px-4 py-2 bg-[#0096FF]/15 border border-[#0096FF]/30 text-[#0096FF] font-semibold rounded-md transition-colors text-sm">
              Platform Settings
            </button>
          </div>
        )}
        {isProducer && (
          <div className="mt-3">
            <button data-testid="disputes-nav-btn" onClick={() => navigate('/disputes')} className="px-4 py-2 bg-[#B91C1C]/10 border border-[#B91C1C]/30 text-[#FCA5A5] font-semibold rounded-md transition-colors text-sm">
              Chargebacks &amp; Disputes
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
